// MENU SECTIONS MIXIN
import {isLocalEnv} from '@/js/global/config';

export const MenuSectionsMixin = {
   methods: {
      /**
       * Check url hash part to go to a specific section
       *
       * It implements a basic routing mechanism based on hashpath
       */
      _goToSectionOnLoad() {
         if(window.location.hash == '') {
            this.$store.commit('goToMenuSection', 0);
            return;
         }

         const section_match = window.location.hash.match(/#\/step-([0-9])/)
         const section = section_match && parseInt(section_match[1]) || 0

         if (!isNaN(section) && section < this.$store.getters.totalMenuSections) {
            this.$store.commit('goToMenuSection', section);
            return;
         }

         this.$store.commit('goToMenuSection', 0);
      },

      /**
       * Get a MenuSection.vue component by its index.
       *
       * @param int sectionIndex
       * @returns {<MenuSection>|null}
       */
      getSection(sectionIndex) {
         return this.menu.sections[sectionIndex] ?? null;
      },

      /* Switch the tabs so the customer sees a specific menu section.
       *
       * If no tab index is provided, then by default it will switch the tabs to one behind.
       * If the current section is the first one, it will take the customer to the Menus
       * Listing page.
       *
       * @param int|null index
       */
      goToSection(index = null) {
         if (typeof index !== 'number') {

            // if no specific index was provided and we're on the first section, take the
            // the customer to the menus listing page.
            if (this.sectionIndex < 1) {
               this.redirectUser(this.menusListingPageUrl);
               return;
            }

            // if we're not going to a specific section, set the index to the section
            // before the current one.
            index = this.sectionIndex - 1;
         }

         // make sure we don't have a negative index
         if (index < 0) {
            index = 0;
         }

         // if the menu is a-la-carte, allow moving forward in steps (not just backwards).
         if (this.menu.isALaCarte) {
            this.navigateToSection(index);
         }

         // for package menus, make sure we're not moving forwards because the customer
         // must select a specific number of items to continue past each step.
         if (index >= this.sectionIndex) {
            return;
         }

         this.scrollToTop();
         this.closeAllModals();
         // move to the selected section and refresh the view for the user
         this.navigateToSection(index);
      },

      /**
       * Increments to the next menu section, which triggers Bootstrap-Vue's tabs to change.
       */
      nextSection() {
         // close any modals that might be open
         this.closeAllModals();
         this.resetAllFilters();

         // bump up the section index to change the tabs
         if (!this.isLastSection) {
            this.navigateToSection(this.menu.sectionIndex+1);
         }
      },

      /**
       * Navigate to a section using the Hash path
       */
      navigateToSection(index) {
         window.location.hash = `#/step-${index}`;
      },

      /**
       * Get a MenuItemControls.vue component from the Vuex store by its slug or variant ID.
       *
       * Note: this may not be the most reactive way to handle things if you need the quantity
       * from the component. For that, you should look in the Vuex state's summary
       * `lineItems`. Use this instead for easily accessing static data.
       *
       * @param variant {string|number|{id: Number, slug: String}}
       * @return {<MenuItemControls>|null}
       */
      getMenuItemComponent(variant) {
         let isVariantSlug = typeof variant === 'string';
         let isVariantId = typeof variant === 'number';

         // check if an object of variant data was passed in
         if (typeof variant === 'object') {
            if (typeof variant.id === 'number') {
               isVariantId = true;
               variant = variant.id;
            }
            else if (typeof variant.slug === 'string') {
               isVariantSlug = true;
               variant = variant.slug
            }
         }

         // search for the MenuItemControls component in the $store by its variant ID or slug
         if (isVariantSlug || isVariantId) {
            for (let key in this.menu.items) {
               if (
                  (isVariantId && this.menu.items[key].variant.id == variant) ||
                  (isVariantSlug && this.menu.items[key].variant.slug === variant)
               ) {
                  return this.menu.items[key];
               }
            }
         }

         return null;
      },
   },
   computed: {

      /**
       * Disable the "Next" button unless the customer's selected the required amount of items.
       *
       * @returns {boolean}
       */
      canGoToNextSection() {
         if (! this.isTemplateDataLoaded || ! this.currentSection) {
            return false;
         }

         // if the customer is on the last step but haven't ordered enough
         if (this.isLastSection && !this.isMinimumOrderValueMet) {
            return false;
         }

         // has all of this section's requirements been met?
         return this.currentSection.isFulfilled;
      },

      /**
       * Get the Vue component for the current section the customer is on.
       *
       * @returns {MenuSection|null}
       */
      currentSection() {
         if (this.isTemplateDataLoaded) {
            try {
               return this.$store.state.menu.sections[this.sectionIndex] ?? null;
            }
            catch (e) {
               if(window.Sentry) {
                  Sentry.captureException(e);
               }
               console.error(e);
            }
         }

         return null;
      },

      /**
       * Detect if the current section is the final one or not.
       *
       * @returns {boolean}
       */
      isLastSection() {
         return this.sectionIndex === (this.totalMenuSections - 1);
      },

      /**
       * Get all the selected items for the current section.
       *
       * @returns {Object}
       */
      currentSectionSelectedItems() {
         if (! this.isTemplateDataLoaded || ! this.currentSection) {
            return {};
         }

         // for package menus' dishes (not extras)
         try {
            if (this.currentSection.isForCourseDishes && ! this.menu.isALaCarte) {
               return {...this.summary.courseItems[this.currentSection.id]};
            }
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            return {};
         }

         // for all menus' line items
         let lineItems = {};
         try {
            // only return line items that are of this section's product type and
            // displayed in this section
            for (let slug in this.lineItems[this.currentSection.productType]) {
               if (this.lineItems[this.currentSection.productType][slug].sectionIndex === this.currentSection.index) {
                  lineItems[slug] = {...this.lineItems[this.currentSection.productType][slug]};
               }
            }
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
         }

         return lineItems;
      },

      /**
       * The number of items the customer has selected in the current section.
       *
       * @return {number}
       */
      currentSectionSelectedItemsCount() {
         try {
            return Object.keys(this.currentSectionSelectedItems).length;
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            return 0;
         }
      },

      /**
       * Change the text on the action button that takes the customer to the next section based
       * on different scenarios.
       *
       * @return {string}
       */
      nextSectionButtonText() {
         if (this.currentSection) {
            // the section requires the customer to select something -OR-
            // if the customer selected something even though they're not required to
            if (this.currentSection.isRequired || this.currentSectionSelectedItemsCount > 0) {
               if (this.isLastSection) {
                  return this.translations.directives.toDeliveryDetails;
               }

               return this.translations.labels.next;
            }

            // if the customer hasn't yet selected something, even though they're not required to
            if (this.isLastSection) {
               return this.translations.directives.toDeliveryDetailsWithoutSelection;
            }
         }

         try {
            return this.translations.directives.continueWithoutSelection;
         }
         catch (e) {
            if(window.Sentry) {
               Sentry.captureException(e);
            }
            console.error(e);
            return '';
         }
      },
   },
}